/**
 * Verify that cached localStorage data belongs to the current logged-in user.
 * Prevents showing old user's data after incomplete logout or device switch.
 * 
 * Returns the parsed data if it belongs to the current user, null otherwise.
 */
export const verifyAndGetCachedStory = () => {
  try {
    const currentUserId = localStorage.getItem('user_id');
    const storyRaw = localStorage.getItem('unified_story_data');
    if (!storyRaw) return null;
    
    const storyData = JSON.parse(storyRaw);
    
    // Check if story data has a user_id that doesn't match current user
    const storyUserId = storyData?.user_id || storyData?.user_info?.user_id;
    if (storyUserId && currentUserId && storyUserId !== currentUserId) {
      console.warn(`⚠️ Stale data detected: story belongs to ${storyUserId}, current user is ${currentUserId}. Clearing.`);
      localStorage.removeItem('unified_story_data');
      localStorage.removeItem('ai_story_data');
      localStorage.removeItem('domain_insights_cache');
      return null;
    }
    
    return storyData;
  } catch (e) {
    return null;
  }
};

/**
 * All timeline-related localStorage keys that must be cleared on user switch.
 */
const TIMELINE_CACHE_KEYS = [
  'timeline_data_cache',
  'real_timeline_data',
  'detailed_monthly_data',
  'detailed_yearly_report',
  'domain_insights_cache',
];

const DOMAIN_KEYS = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];

/**
 * Clear ALL timeline caches from localStorage.
 * Call this on user switch / logout to prevent data leakage.
 */
export const clearAllTimelineCaches = () => {
  TIMELINE_CACHE_KEYS.forEach(k => localStorage.removeItem(k));
  DOMAIN_KEYS.forEach(d => localStorage.removeItem(`${d}_timeline_data`));
  console.log('Cleared all timeline caches');
};

/**
 * Read a timeline cache key from localStorage, but ONLY if it belongs to the current user.
 * Cache entries are expected to have a `_userId` field stamped when stored.
 * If no `_userId` field exists (old cache), it's treated as stale and removed.
 * Returns parsed data or null.
 */
export const getVerifiedTimelineCache = (cacheKey) => {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const currentUserId = localStorage.getItem('user_id');
    // If cache has a _userId stamp, verify it matches
    if (parsed?._userId && currentUserId && parsed._userId !== currentUserId) {
      console.warn(`Stale timeline cache "${cacheKey}": belongs to ${parsed._userId}, current user is ${currentUserId}. Clearing.`);
      localStorage.removeItem(cacheKey);
      return null;
    }
    // If cache has no _userId stamp but user is logged in, treat as potentially stale
    if (!parsed?._userId && currentUserId) {
      console.warn(`Timeline cache "${cacheKey}" has no user stamp. Clearing for safety.`);
      localStorage.removeItem(cacheKey);
      return null;
    }
    return parsed;
  } catch (e) {
    localStorage.removeItem(cacheKey);
    return null;
  }
};

/**
 * Store a timeline cache entry with user_id stamp for future verification.
 */
export const setVerifiedTimelineCache = (cacheKey, data) => {
  try {
    const currentUserId = localStorage.getItem('user_id');
    const stamped = { ...data, _userId: currentUserId || null };
    localStorage.setItem(cacheKey, JSON.stringify(stamped));
  } catch (e) {
    // Storage quota or other error — silently fail
  }
};
